import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './config';

// Interfaces for Firestore documents
export interface PromptDocument {
  id?: string;
  userId: string;
  title: string;
  content: string;
  platform: string;
  category: string;
  tags?: string[];
  parameters?: Record<string, any>;
  isPublic: boolean;
  isFavorite: boolean;
  views?: number;
  likes?: number;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

export interface TemplateDocument {
  id?: string;
  userId: string;
  name: string;
  description: string;
  template: string;
  variables: string[];
  platform: string;
  category: string;
  isPublic: boolean;
  uses?: number;
  rating?: number;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

export interface HistoryDocument {
  id?: string;
  userId: string;
  promptId?: string;
  action: 'generated' | 'saved' | 'exported' | 'shared';
  content: string;
  platform?: string;
  metadata?: Record<string, any>;
  timestamp: Date | Timestamp;
}

// Generic CRUD operations
class FirestoreService {
  // Create document
  async create<T extends DocumentData>(
    collectionName: string,
    data: T,
    customId?: string
  ): Promise<string> {
    try {
      const docRef = customId 
        ? doc(db, collectionName, customId)
        : doc(collection(db, collectionName));
      
      await setDoc(docRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error: any) {
      throw new Error(`Failed to create document: ${error.message}`);
    }
  }

  // Read document
  async get<T>(collectionName: string, docId: string): Promise<T | null> {
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      }
      
      return null;
    } catch (error: any) {
      throw new Error(`Failed to get document: ${error.message}`);
    }
  }

  // Update document
  async update<T extends DocumentData>(
    collectionName: string,
    docId: string,
    data: Partial<T>
  ): Promise<void> {
    try {
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error: any) {
      throw new Error(`Failed to update document: ${error.message}`);
    }
  }

  // Delete document
  async delete(collectionName: string, docId: string): Promise<void> {
    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
    } catch (error: any) {
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  }

  // List documents with pagination
  async list<T>(
    collectionName: string,
    filters: { field: string; operator: any; value: any }[] = [],
    sortBy?: { field: string; direction: 'asc' | 'desc' },
    limitCount: number = 20,
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{ data: T[]; lastDoc: QueryDocumentSnapshot | null }> {
    try {
      let q = query(collection(db, collectionName));
      
      // Apply filters
      filters.forEach(filter => {
        q = query(q, where(filter.field, filter.operator, filter.value));
      });
      
      // Apply sorting
      if (sortBy) {
        q = query(q, orderBy(sortBy.field, sortBy.direction));
      }
      
      // Apply pagination
      q = query(q, limit(limitCount));
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const querySnapshot = await getDocs(q);
      const data: T[] = [];
      let last: QueryDocumentSnapshot | null = null;
      
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as T);
        last = doc;
      });
      
      return { data, lastDoc: last };
    } catch (error: any) {
      throw new Error(`Failed to list documents: ${error.message}`);
    }
  }

  // Subscribe to real-time updates
  subscribe<T>(
    collectionName: string,
    docId: string,
    callback: (data: T | null) => void
  ): Unsubscribe {
    const docRef = doc(db, collectionName, docId);
    
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() } as T);
      } else {
        callback(null);
      }
    });
  }

  // Subscribe to collection changes
  subscribeToCollection<T>(
    collectionName: string,
    filters: { field: string; operator: any; value: any }[] = [],
    callback: (data: T[]) => void
  ): Unsubscribe {
    let q = query(collection(db, collectionName));
    
    filters.forEach(filter => {
      q = query(q, where(filter.field, filter.operator, filter.value));
    });
    
    return onSnapshot(q, (querySnapshot) => {
      const data: T[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as T);
      });
      callback(data);
    });
  }
}

// Specific services for each collection
export const promptsService = {
  // Create a new prompt
  create: async (prompt: Omit<PromptDocument, 'id' | 'createdAt' | 'updatedAt'>) => {
    return firestoreService.create('prompts', prompt);
  },

  // Get a prompt by ID
  get: async (promptId: string) => {
    return firestoreService.get<PromptDocument>('prompts', promptId);
  },

  // Update a prompt
  update: async (promptId: string, updates: Partial<PromptDocument>) => {
    return firestoreService.update('prompts', promptId, updates);
  },

  // Delete a prompt
  delete: async (promptId: string) => {
    return firestoreService.delete('prompts', promptId);
  },

  // Get user's prompts
  getUserPrompts: async (userId: string, limitCount: number = 20) => {
    // Simplified query that doesn't require an index
    return firestoreService.list<PromptDocument>(
      'prompts',
      [{ field: 'userId', operator: '==', value: userId }],
      undefined, // Remove sorting to avoid index requirement
      limitCount
    );
  },

  // Get public prompts
  getPublicPrompts: async (limitCount: number = 20) => {
    return firestoreService.list<PromptDocument>(
      'prompts',
      [{ field: 'isPublic', operator: '==', value: true }],
      { field: 'likes', direction: 'desc' },
      limitCount
    );
  },

  // Toggle favorite
  toggleFavorite: async (promptId: string, isFavorite: boolean) => {
    return firestoreService.update('prompts', promptId, { isFavorite });
  },

  // Increment views
  incrementViews: async (promptId: string) => {
    const prompt = await firestoreService.get<PromptDocument>('prompts', promptId);
    if (prompt) {
      return firestoreService.update('prompts', promptId, {
        views: (prompt.views || 0) + 1
      });
    }
  }
};

export const templatesService = {
  // Create a new template
  create: async (template: Omit<TemplateDocument, 'id' | 'createdAt' | 'updatedAt'>) => {
    return firestoreService.create('templates', template);
  },

  // Get a template by ID
  get: async (templateId: string) => {
    return firestoreService.get<TemplateDocument>('templates', templateId);
  },

  // Update a template
  update: async (templateId: string, updates: Partial<TemplateDocument>) => {
    return firestoreService.update('templates', templateId, updates);
  },

  // Delete a template
  delete: async (templateId: string) => {
    return firestoreService.delete('templates', templateId);
  },

  // Get user's templates
  getUserTemplates: async (userId: string) => {
    return firestoreService.list<TemplateDocument>(
      'templates',
      [{ field: 'userId', operator: '==', value: userId }],
      undefined // Remove sorting to avoid index requirement
    );
  },

  // Get public templates
  getPublicTemplates: async (category?: string) => {
    const filters = [{ field: 'isPublic', operator: '==', value: true }];
    if (category) {
      filters.push({ field: 'category', operator: '==', value: category });
    }
    
    return firestoreService.list<TemplateDocument>(
      'templates',
      filters,
      { field: 'rating', direction: 'desc' }
    );
  },

  // Increment template usage
  incrementUses: async (templateId: string) => {
    const template = await firestoreService.get<TemplateDocument>('templates', templateId);
    if (template) {
      return firestoreService.update('templates', templateId, {
        uses: (template.uses || 0) + 1
      });
    }
  }
};

export const historyService = {
  // Add history entry
  add: async (entry: Omit<HistoryDocument, 'id' | 'timestamp'>) => {
    return firestoreService.create('history', {
      ...entry,
      timestamp: serverTimestamp()
    });
  },

  // Get user's history
  getUserHistory: async (userId: string, limitCount: number = 50) => {
    return firestoreService.list<HistoryDocument>(
      'history',
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'timestamp', direction: 'desc' },
      limitCount
    );
  },

  // Clear user's history
  clearUserHistory: async (userId: string) => {
    const { data } = await firestoreService.list<HistoryDocument>(
      'history',
      [{ field: 'userId', operator: '==', value: userId }]
    );
    
    const deletePromises = data.map(item => 
      item.id ? firestoreService.delete('history', item.id) : Promise.resolve()
    );
    
    await Promise.all(deletePromises);
  },

  // Get history by action type
  getByAction: async (userId: string, action: HistoryDocument['action']) => {
    return firestoreService.list<HistoryDocument>(
      'history',
      [
        { field: 'userId', operator: '==', value: userId },
        { field: 'action', operator: '==', value: action }
      ],
      { field: 'timestamp', direction: 'desc' }
    );
  }
};

// Export the main service instance
const firestoreService = new FirestoreService();
export default firestoreService;